import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStores } from "@/context/StoreContext";
import { PageHeader, StatCard, Loading, StatusBadge, EmptyState } from "@/components/common";
import PeriodFilter, { usePeriod, PERIOD_SUFFIX } from "@/components/PeriodFilter";
import { rupiah, numberID } from "@/lib/format";
import {
  ShoppingCart, TrendingUp, Package, Boxes, Megaphone, AlertTriangle,
  Warehouse, Factory, Printer, Lock, MessageSquareWarning, RotateCcw, Target, LineChart as LineChartIcon,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Dashboard() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { activeStore, stores } = useStores();
  const { period, start_date, end_date, setPeriod } = usePeriod();
  const isOwner = can("dashboard.owner");
  const [owner, setOwner] = useState(null);
  const [ops, setOps] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [perf, setPerf] = useState([]);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = { store_id: activeStore, period };
  if (period === "custom") { params.start_date = start_date; params.end_date = end_date; }
  const paramKey = JSON.stringify(params);

  useEffect(() => {
    if (period === "custom" && (!start_date || !end_date)) { setLoading(false); return; }
    setLoading(true);
    const calls = [];
    if (isOwner) {
      calls.push(api.get("/dashboard/owner", { params }).then((r) => setOwner(r.data)).catch(() => {}));
      calls.push(api.get("/analytics/stores-performance", { params }).then((r) => setPerf(r.data)).catch(() => {}));
      calls.push(api.get("/dashboard/ops-summary", { params }).then((r) => setOps(r.data)).catch(() => {}));
    }
    calls.push(api.get("/dashboard/admin", { params }).then((r) => setAdmin(r.data)).catch(() => {}));
    calls.push(api.get("/analytics/sales-trend", { params }).then((r) => setTrend(r.data)).catch(() => {}));
    Promise.all(calls).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKey, isOwner]);

  const storeName = activeStore === "all" ? "Semua Toko" : (stores.find((s) => s.id === activeStore)?.name || "");
  const suf = PERIOD_SUFFIX[period] || "Periode";
  const m = admin?.metrics;
  const stock = owner?.stock || admin?.stock;
  const roas = owner?.roas ?? admin?.roas ?? 0;
  const adSpend = owner?.ad_spend ?? admin?.ad_spend ?? 0;
  const complaints = owner?.complaints_period ?? admin?.complaints_open ?? 0;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="dashboard-title">
            {isOwner ? "Owner Command Center" : "Dashboard Admin Online Shop"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isOwner ? "Ringkasan operasional seluruh divisi SPH Ascendra" : `Data penjualan: ${storeName}`}
            {activeStore !== "all" && isOwner ? ` · ${storeName}` : ""}
          </p>
        </div>
        <PeriodFilter period={period} start_date={start_date} end_date={end_date} onChange={setPeriod} />
      </div>

      {loading ? <Loading label="Memuat dashboard..." /> : (
      <>
      {isOwner && ops && (
        <div className="mb-8" data-testid="ops-summary">
          <div className="mb-3 flex items-center gap-2">
            <Factory className="h-4 w-4 text-indigo-600" />
            <h3 className="font-display text-lg font-semibold text-slate-900">Ringkasan Operasional</h3>
            <span className="text-xs text-slate-400">Produksi & Purchasing · detail di dashboard masing-masing</span>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <button onClick={() => navigate("/production/dashboard")} className="text-left" data-testid="ops-produksi"><StatCard label="Produksi Aktif" value={numberID(ops.produksi_aktif)} icon={Factory} accent="indigo" /></button>
            <StatCard label={`Achievement Produksi ${suf}`} value={`${ops.achievement}%`} icon={Target} accent="emerald" testId="ops-achievement" />
            <button onClick={() => navigate("/production/dashboard")} className="text-left" data-testid="ops-qc"><StatCard label="QC Pending" value={numberID(ops.qc_pending)} icon={Package} accent="amber" /></button>
            <button onClick={() => navigate("/production/dashboard")} className="text-left" data-testid="ops-shortage"><StatCard label="Material Shortage" value={numberID(ops.material_shortage)} icon={AlertTriangle} accent="rose" hint={`Potensi: ${numberID(ops.material_shortage_potensi || 0)}`} /></button>
            <StatCard label="Barang Jadi Menunggu Transfer" value={numberID(ops.fg_waiting_transfer)} icon={Boxes} accent="sky" testId="ops-fg" />
            <button onClick={() => navigate("/purchasing/dashboard")} className="text-left" data-testid="ops-pr"><StatCard label="PR Pending" value={numberID(ops.pr_pending)} icon={MessageSquareWarning} accent="amber" /></button>
            <button onClick={() => navigate("/purchasing/dashboard")} className="text-left" data-testid="ops-po"><StatCard label="PO Outstanding" value={numberID(ops.po_outstanding)} icon={ShoppingCart} accent="indigo" /></button>
            <button onClick={() => navigate("/notifications")} className="text-left" data-testid="ops-alerts"><StatCard label="Critical Alerts" value={numberID(ops.critical_alerts)} icon={AlertTriangle} accent="rose" /></button>
          </div>
        </div>
      )}
      {/* Sales KPI cards (store + period filtered) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={`Pesanan ${suf}`} value={numberID(m?.orders_period)} icon={ShoppingCart} accent="indigo" testId="kpi-orders" />
        <StatCard label={`Omzet ${suf}`} value={rupiah(m?.revenue_period)} icon={TrendingUp} accent="emerald" testId="kpi-revenue" />
        <StatCard label={`PCS Terjual ${suf}`} value={numberID(m?.pcs_period)} icon={Package} accent="sky" testId="kpi-pcs" />
        <StatCard label={`Biaya Iklan ${suf}`} value={rupiah(adSpend)} icon={Megaphone} accent="amber" testId="kpi-adspend" />
      </div>

      {/* Secondary KPIs */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={`ROAS ${suf}`} value={`${roas}x`} icon={Target} accent="indigo" testId="kpi-roas" />
        <StatCard label={`Komplain ${suf}`} value={numberID(complaints)} icon={MessageSquareWarning} accent="rose" testId="kpi-complaints" />
        <StatCard label={`Retur ${suf}`} value={numberID(m?.returns)} icon={RotateCcw} accent="amber" testId="kpi-returns" />
        <StatCard label={`Pesanan Dibatalkan ${suf}`} value={numberID(m?.cancelled)} icon={AlertTriangle} accent="slate" testId="kpi-cancelled" />
      </div>

      {/* Order status breakdown (period-scoped) */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
        {["Baru", "Diproses", "Siap Dikirim", "Dikirim", "Selesai", "Dibatalkan", "Return"].map((s) => (
          <div key={s} className="rounded-lg border border-slate-200 bg-white p-3 text-center" data-testid={`status-${s}`}>
            <StatusBadge status={s} />
            <p className="mt-2 font-display text-xl font-bold text-slate-900">{numberID(m?.by_status?.[s])}</p>
          </div>
        ))}
      </div>

      {/* Sales trend */}
      <div className="mt-8">
        <div className="mb-3 flex items-center gap-2">
          <LineChartIcon className="h-4 w-4 text-indigo-600" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Tren Penjualan</h3>
          <span className="text-xs text-slate-400">Omzet, Pesanan & PCS · {suf}</span>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          {!trend ? <Loading /> : !trend.has_data ? (
            <EmptyState title="Belum ada penjualan pada periode ini" subtitle="Pilih periode lain atau catat pesanan baru." icon={LineChartIcon} />
          ) : (
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <LineChart data={trend.points} margin={{ top: 10, right: 16, left: -6, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${v / 1000}k`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v, n) => n === "Omzet" ? rupiah(v) : numberID(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="left" type="monotone" dataKey="omzet" name="Omzet" stroke="#4F46E5" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="orders" name="Pesanan" stroke="#10B981" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="pcs" name="PCS" stroke="#F59E0B" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Current inventory snapshot (NOT summed over the period) */}
      <div className="mt-8">
        <div className="mb-3 flex items-center gap-2">
          <Warehouse className="h-4 w-4 text-indigo-600" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Posisi Stok Saat Ini</h3>
          <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600">Snapshot stok terkini · tidak mengikuti filter periode</span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Stok Fisik (On Hand)" value={numberID(stock?.on_hand)} icon={Boxes} accent="slate" hint="Barang jadi di gudang pusat" testId="kpi-onhand" />
          <StatCard label="Stok Direservasi" value={numberID(stock?.reserved)} icon={Lock} accent="amber" testId="kpi-reserved" />
          <StatCard label="Stok Tersedia" value={numberID(stock?.available)} icon={Package} accent="emerald" testId="kpi-available" />
          <StatCard label="Low / Out of Stock" value={`${numberID(stock?.low_stock)} / ${numberID(stock?.out_of_stock)}`} icon={AlertTriangle} accent="rose" testId="kpi-lowstock" />
        </div>
      </div>

      {isOwner && owner && (
        <>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard label="Nilai Persediaan Pusat" value={rupiah(owner.inventory_value)} icon={Warehouse} accent="indigo" hint="Berdasarkan HPP · snapshot" testId="kpi-inv-value" />
            {owner.warehouses.map((w) => {
              const Icon = w.type === "PUSAT" ? Warehouse : w.type === "KONVEKSI" ? Factory : Printer;
              return (
                <div key={w.type} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white"><Icon className="h-5 w-5" /></div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{w.warehouse}</p>
                      <p className="text-xs text-slate-400">{w.skus} item · {numberID(w.qty)} unit</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Store performance (period-scoped) */}
          <div className="mt-8">
            <h3 className="mb-3 font-display text-lg font-semibold text-slate-900">
              {activeStore === "all" ? "Performa Semua Toko" : `Performa Toko — ${storeName}`} <span className="text-sm font-normal text-slate-400">· {suf}</span>
            </h3>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={perf} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                    <XAxis dataKey="store_name" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-20} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${v / 1000}k`} />
                    <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Bar dataKey="omzet" radius={[6, 6, 0, 0]} fill="#4F46E5" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Toko</TableHead>
                      <TableHead className="text-right">Order</TableHead>
                      <TableHead className="text-right">PCS</TableHead>
                      <TableHead className="text-right">Omzet</TableHead>
                      <TableHead className="text-right">Biaya Iklan</TableHead>
                      <TableHead className="text-right">ROAS</TableHead>
                      <TableHead className="text-right">Komplain</TableHead>
                      <TableHead className="text-right">Retur</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {perf.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-400 py-6">Belum ada data pada periode ini</TableCell></TableRow> :
                      perf.map((r) => (
                        <TableRow key={r.store_id} data-testid={`perf-row-${r.store_id}`}>
                          <TableCell className="font-medium">{r.store_name}</TableCell>
                          <TableCell className="text-right">{numberID(r.orders)}</TableCell>
                          <TableCell className="text-right">{numberID(r.pcs)}</TableCell>
                          <TableCell className="text-right">{rupiah(r.omzet)}</TableCell>
                          <TableCell className="text-right">{rupiah(r.ad_spend)}</TableCell>
                          <TableCell className="text-right font-semibold">{r.roas}x</TableCell>
                          <TableCell className="text-right">{r.complaints}</TableCell>
                          <TableCell className="text-right">{r.returns}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}
